import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLayerGroup,
  faCircleQuestion,
  faCircleInfo,
  faCodeBranch,
  faTag,
  faSitemap,
  faWandMagicSparkles,
  faLink,
  faMagnifyingGlass,
  faPlus,
  faArrowUp,
  faXmark,
  faBan,
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchQueue,
  fetchNodes,
  fetchAliases,
  fetchEdges,
  fetchSuggestions,
  linkSurface,
  mintConcreteForSurface,
  mintSenseForSurface,
  resyncOrphans,
  rejectSurface,
  repointAlias,
  splitAlias,
  setAliasActive,
  relabelNode,
  setNodeDomain,
  addParentEdge,
  removeParentEdge,
  fetchReextractionStatus,
  triggerReextraction,
} from '../api/skillsTaxonomy';
import type {
  CurationQueue,
  SkillNode,
  SkillAlias,
  SkillEdge,
  SuggestedNode,
  UnresolvedItem,
  DomainlessAliasItem,
  UnresolvedKind,
  ReextractionStatus,
} from '../api/skillsTaxonomy';

type Tab = 'queue' | 'aliases' | 'nodes';
type ItemKind = UnresolvedKind | 'DOMAINLESS_ALIAS';
type RunFn = (action: () => Promise<void>, message: string) => Promise<void>;

// A unified left-column row across the three queue families.
type QueueRow =
  | { kind: UnresolvedKind; key: string; surface: string; item: UnresolvedItem }
  | { kind: 'DOMAINLESS_ALIAS'; key: string; surface: string; item: DomainlessAliasItem };

const KIND_META: Record<ItemKind, { border: string; badge: string; labelKey: string }> = {
  ORPHAN: { border: 'border-l-blue-400', badge: 'bg-blue-50 text-blue-700', labelKey: 'skillsTaxonomy.kindOrphan' },
  DOMAINLESS_ORPHAN: { border: 'border-l-amber-400', badge: 'bg-amber-50 text-amber-700', labelKey: 'skillsTaxonomy.kindDomainlessOrphan' },
  DOMAINLESS_ALIAS: { border: 'border-l-purple-400', badge: 'bg-purple-50 text-purple-700', labelKey: 'skillsTaxonomy.kindDomainlessAlias' },
};

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function sourceBadgeClass(type: string): string {
  if (type === 'CV') return 'bg-blue-50 text-blue-700';
  if (type === 'TENDER') return 'bg-purple-50 text-purple-700';
  return 'bg-gray-100 text-gray-600';
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function SkillsTaxonomy() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('queue');
  const [queue, setQueue] = useState<CurationQueue | null>(null);
  const [nodes, setNodes] = useState<SkillNode[]>([]);
  const [aliases, setAliases] = useState<SkillAlias[]>([]);
  const [edges, setEdges] = useState<SkillEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // The curation switcher (versioning spec §6): scopes ONLY the orphan queue to a
  // prompt-version cohort. Empty = the production version (the live view).
  const [queueVersion, setQueueVersion] = useState('');
  // Candidate cohort re-extraction status (versioning spec §7): the candidate version
  // the "Re-extract all" button grows, and whether a run is in progress.
  const [reextract, setReextract] = useState<ReextractionStatus | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [q, n, a, e, rx] = await Promise.all([
        fetchQueue(queueVersion.trim() || undefined), fetchNodes(), fetchAliases(), fetchEdges(),
        fetchReextractionStatus(),
      ]);
      setQueue(q);
      setNodes(n);
      setAliases(a);
      setEdges(e);
      setReextract(rx);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('skillsTaxonomy.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, queueVersion]);

  useEffect(() => {
    void load();
  }, [load]);

  const nodeLabel = useMemo(() => {
    const map = new Map(nodes.map((n) => [n.id, n.canonical_label]));
    return (id: string | null) => (id ? map.get(id) ?? id : '—');
  }, [nodes]);

  const run = useCallback<RunFn>(
    async (action, message) => {
      setBusy(true);
      setError(null);
      setSuccess(null);
      try {
        await action();
        setSuccess(message);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : t('skillsTaxonomy.actionFailed'));
      } finally {
        setBusy(false);
      }
    },
    [load, t],
  );

  const startReextraction = useCallback(async () => {
    if (!window.confirm(t('skillsTaxonomy.reextractConfirm', { version: reextract?.candidate_version ?? '' }))) {
      return;
    }
    await run(async () => {
      const status = await triggerReextraction();
      setReextract(status);
    }, t('skillsTaxonomy.reextractStarted'));
  }, [reextract, run, t]);

  const counts = {
    orphan: queue?.orphans.length ?? 0,
    domainlessOrphan: queue?.domainless_orphans.length ?? 0,
    domainlessAlias: queue?.domainless_aliases.length ?? 0,
    parentless: queue?.parentless_nodes.length ?? 0,
  };
  const inQueue = counts.orphan + counts.domainlessOrphan + counts.domainlessAlias;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{t('skillsTaxonomy.title')}</h1>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{t('skillsTaxonomy.curationPill')}</span>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('skillsTaxonomy.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Curation switcher (versioning spec §6): scopes only the orphan queue to a
              prompt-version cohort. Empty → production. Applied on Enter or blur. */}
          <label className="flex items-center gap-1.5 text-sm text-gray-600" title={t('skillsTaxonomy.queueVersionHint')}>
            <span className="whitespace-nowrap">{t('skillsTaxonomy.queueVersionLabel')}</span>
            <input
              type="text"
              defaultValue={queueVersion}
              placeholder={t('skillsTaxonomy.queueVersionProduction')}
              disabled={busy || loading}
              onKeyDown={(ev) => { if (ev.key === 'Enter') setQueueVersion((ev.target as HTMLInputElement).value.trim()); }}
              onBlur={(ev) => setQueueVersion(ev.target.value.trim())}
              className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
            />
          </label>
          {/* Grow the candidate cohort (versioning spec §7): re-extract the whole
              corpus as the candidate version, leaving production untouched. Shows the
              candidate version; disabled while a run is in progress. */}
          {reextract && (
            <button type="button" onClick={() => void startReextraction()}
              disabled={busy || reextract.running}
              title={t('skillsTaxonomy.reextractHint', { version: reextract.candidate_version })}
              className="rounded-md border border-brand-300 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50">
              {reextract.running
                ? t('skillsTaxonomy.reextractRunning', { version: reextract.candidate_version })
                : t('skillsTaxonomy.reextract', { version: reextract.candidate_version })}
            </button>
          )}
          <button type="button" onClick={() => void run(async () => { await resyncOrphans(); }, t('skillsTaxonomy.resyncDone'))} disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {t('skillsTaxonomy.resync')}
          </button>
          <button type="button" onClick={() => void load()} disabled={busy || loading}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard icon={faLayerGroup} iconColor="text-gray-400" label={t('skillsTaxonomy.statInQueue')} value={inQueue} />
        <StatCard icon={faCircleQuestion} iconColor="text-blue-500" label={t('skillsTaxonomy.kindOrphan')} value={counts.orphan} />
        <StatCard icon={faCodeBranch} iconColor="text-amber-500" label={t('skillsTaxonomy.kindDomainlessOrphan')} value={counts.domainlessOrphan} />
        <StatCard icon={faTag} iconColor="text-purple-500" label={t('skillsTaxonomy.kindDomainlessAlias')} value={counts.domainlessAlias} />
        <StatCard icon={faSitemap} iconColor="text-gray-400" label={t('skillsTaxonomy.statParentless')} value={counts.parentless} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <TabButton active={tab === 'queue'} onClick={() => setTab('queue')} label={t('skillsTaxonomy.tabQueue')} />
        <TabButton active={tab === 'aliases'} onClick={() => setTab('aliases')} label={t('skillsTaxonomy.tabAliases')} />
        <TabButton active={tab === 'nodes'} onClick={() => setTab('nodes')} label={t('skillsTaxonomy.tabNodes')} />
      </div>

      {/* Banners */}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700" role="status">{success}</div>}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-2 font-medium text-red-500 hover:text-red-700" aria-label={t('common.close')}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16" role="status">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
          <span className="sr-only">{t('common.loading')}</span>
        </div>
      ) : (
        <>
          {tab === 'queue' && queue && <QueueTab queue={queue} nodes={nodes} busy={busy} nodeLabel={nodeLabel} run={run} />}
          {tab === 'aliases' && <AliasesTab aliases={aliases} nodes={nodes} busy={busy} nodeLabel={nodeLabel} run={run} />}
          {tab === 'nodes' && <NodesTab nodes={nodes} edges={edges} busy={busy} nodeLabel={nodeLabel} run={run} />}
        </>
      )}
    </div>
  );
}

function StatCard({ icon, iconColor, label, value }: { icon: IconDefinition; iconColor: string; label: string; value: number }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        <FontAwesomeIcon icon={icon} className={iconColor} />
        {label}
      </div>
      <div className="mt-1 text-3xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${active ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Curation Queue tab — master/detail
// ---------------------------------------------------------------------------

// Filter pills: by item kind, plus by orphan source (CV vs Tender). The source
// filters keep any orphan-family row whose observations came from that source.
type QueueFilter = 'ALL' | ItemKind | 'SRC_CV' | 'SRC_TENDER';

const FILTERS: { value: QueueFilter; labelKey: string }[] = [
  { value: 'ALL', labelKey: 'skillsTaxonomy.filterAll' },
  { value: 'ORPHAN', labelKey: 'skillsTaxonomy.kindOrphan' },
  { value: 'DOMAINLESS_ORPHAN', labelKey: 'skillsTaxonomy.kindDomainlessOrphan' },
  { value: 'DOMAINLESS_ALIAS', labelKey: 'skillsTaxonomy.kindDomainlessAlias' },
  { value: 'SRC_CV', labelKey: 'skillsTaxonomy.filterOrphansFromCv' },
  { value: 'SRC_TENDER', labelKey: 'skillsTaxonomy.filterOrphansFromTender' },
];

function QueueTab({ queue, nodes, busy, nodeLabel, run }: { queue: CurationQueue; nodes: SkillNode[]; busy: boolean; nodeLabel: (id: string | null) => string; run: RunFn }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<QueueFilter>('ALL');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const rows = useMemo<QueueRow[]>(() => {
    const orphans: QueueRow[] = queue.orphans.map((item) => ({ kind: 'ORPHAN', key: `ORPHAN:${item.surface}`, surface: item.surface, item }));
    const dOrphans: QueueRow[] = queue.domainless_orphans.map((item) => ({ kind: 'DOMAINLESS_ORPHAN', key: `DORPHAN:${item.surface}`, surface: item.surface, item }));
    const dAliases: QueueRow[] = queue.domainless_aliases.map((item) => ({ kind: 'DOMAINLESS_ALIAS', key: `DALIAS:${item.alias_id}`, surface: item.surface_form, item }));
    return [...orphans, ...dOrphans, ...dAliases];
  }, [queue]);

  const visible = ((): QueueRow[] => {
    if (filter === 'ALL') return rows;
    if (filter === 'SRC_CV' || filter === 'SRC_TENDER') {
      const source = filter === 'SRC_CV' ? 'CV' : 'TENDER';
      // Orphan-family rows carry source_types; domainless aliases don't.
      return rows.filter((r) => r.kind !== 'DOMAINLESS_ALIAS'
        && (r.item as UnresolvedItem).source_types.includes(source));
    }
    return rows.filter((r) => r.kind === filter);
  })();
  const selected = rows.find((r) => r.key === selectedKey) ?? null;

  return (
    <div className="space-y-3">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.value} type="button" onClick={() => setFilter(f.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === f.value ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Left list */}
        <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
          {visible.length === 0 ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-400">{t('skillsTaxonomy.queueEmpty')}</p>
          ) : (
            visible.map((row) => <QueueCard key={row.key} row={row} selected={row.key === selectedKey} onSelect={() => setSelectedKey(row.key)} />)
          )}
        </div>

        {/* Right detail */}
        <div className="rounded-xl border border-gray-200 bg-white">
          {!selected ? (
            <div className="flex h-full min-h-[16rem] items-center justify-center p-8 text-center text-sm text-gray-400">{t('skillsTaxonomy.selectItem')}</div>
          ) : selected.kind === 'DOMAINLESS_ALIAS' ? (
            <DomainlessAliasDetail item={selected.item as DomainlessAliasItem} busy={busy} nodeLabel={nodeLabel} run={run} />
          ) : (
            <OrphanDetail row={selected as Extract<QueueRow, { kind: UnresolvedKind }>} nodes={nodes} busy={busy} run={run} />
          )}
        </div>
      </div>
    </div>
  );
}

function QueueCard({ row, selected, onSelect }: { row: QueueRow; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const meta = KIND_META[row.kind];
  const isUnresolved = row.kind !== 'DOMAINLESS_ALIAS';
  const u = isUnresolved ? (row.item as UnresolvedItem) : null;
  const a = !isUnresolved ? (row.item as DomainlessAliasItem) : null;

  return (
    <button type="button" onClick={onSelect}
      className={`block w-full rounded-lg border border-l-4 bg-white px-4 py-3 text-left ${meta.border} ${selected ? 'border-brand-400 ring-1 ring-brand-200' : 'border-gray-200 hover:border-gray-300'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[15px] font-medium text-gray-900">&ldquo;{row.surface}&rdquo;</span>
        <span className="flex items-center gap-1.5">
          {u && u.original_texts.length > 0 && (
            <span
              className="cursor-help text-gray-300 hover:text-gray-500"
              aria-label={t('skillsTaxonomy.originalText')}
              title={`${t('skillsTaxonomy.originalText')}:\n\n${u.original_texts.join('\n\n')}`}
            >
              <FontAwesomeIcon icon={faCircleInfo} />
            </span>
          )}
          <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${meta.badge}`}>{t(meta.labelKey)}</span>
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          {u?.source_types.map((s) => (
            <span key={s} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${sourceBadgeClass(s)}`}>{s}</span>
          ))}
          {u && <span>{t('skillsTaxonomy.occurrences', { count: u.observation_count })}</span>}
          {a && a.sibling_node_labels.length > 0 && <span>{t('skillsTaxonomy.siblingsCount', { count: a.sibling_node_labels.length })}</span>}
        </div>
        {u && <span>{t('skillsTaxonomy.ago', { time: timeAgo(u.last_seen) })}</span>}
      </div>
    </button>
  );
}

// ---------- Orphan / Domainless-orphan detail ----------

function OrphanDetail({ row, nodes, busy, run }: { row: Extract<QueueRow, { kind: UnresolvedKind }>; nodes: SkillNode[]; busy: boolean; run: RunFn }) {
  const { t } = useTranslation();
  const surface = row.surface;
  const isSense = row.kind === 'DOMAINLESS_ORPHAN';
  const meta = KIND_META[row.kind];
  const item = row.item;

  const [suggestions, setSuggestions] = useState<SuggestedNode[]>([]);
  const [search, setSearch] = useState('');
  const [mintLabel, setMintLabel] = useState('');

  useEffect(() => {
    let active = true;
    setSuggestions([]);
    fetchSuggestions(surface).then((s) => { if (active) setSuggestions(s); }).catch(() => {});
    setSearch('');
    setMintLabel('');
    return () => { active = false; };
  }, [surface]);

  const matches = search.trim()
    ? nodes.filter((n) => n.canonical_label.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  const doLink = (nodeId: string) => run(async () => { await linkSurface(surface, nodeId); }, t('skillsTaxonomy.linked'));
  const doMint = () => run(async () => {
    if (isSense) await mintSenseForSurface(surface, mintLabel.trim());
    else await mintConcreteForSurface(surface, mintLabel.trim());
  }, t('skillsTaxonomy.minted'));
  const doReject = () => {
    if (!window.confirm(t('skillsTaxonomy.rejectConfirm', { surface }))) return;
    return run(async () => { await rejectSurface(surface); }, t('skillsTaxonomy.rejectDone'));
  };

  return (
    <div className="space-y-4 p-5">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        {item.source_types.map((s) => <span key={s} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${sourceBadgeClass(s)}`}>{s}</span>)}
        <span>{t('skillsTaxonomy.extractedAgo', { time: timeAgo(item.last_seen) })}</span>
      </div>
      <div className="flex items-center gap-2">
        <h2 className="font-mono text-xl font-semibold text-gray-900">&ldquo;{surface}&rdquo;</h2>
        <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${meta.badge}`}>{t(meta.labelKey)}</span>
      </div>

      <div className={`rounded-lg border p-3 text-sm ${isSense ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
        {isSense ? t('skillsTaxonomy.domainlessOrphanCallout') : t('skillsTaxonomy.orphanCallout')}
      </div>

      {/* Suggested matches */}
      {suggestions.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.suggestedMatches')}</p>
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div key={s.node_id} className="flex items-center justify-between gap-2 rounded-lg border border-green-100 bg-green-50/60 px-3 py-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="text-green-600" />
                  <span className="text-sm font-medium text-gray-900">{s.label}</span>
                  <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{s.kind.toLowerCase()}</span>
                </div>
                <button type="button" disabled={busy} onClick={() => void doLink(s.node_id)}
                  className="flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                  <FontAwesomeIcon icon={faLink} /> {t('skillsTaxonomy.link')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to existing node — search */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.linkToExisting')}</p>
        <div className="relative">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-2.5 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('skillsTaxonomy.searchCanonical')}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        {matches.length > 0 && (
          <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200">
            {matches.map((n) => (
              <button key={n.id} type="button" disabled={busy} onClick={() => void doLink(n.id)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50">
                <span className="text-gray-800">{n.canonical_label}</span>
                <span className="text-xs text-gray-400">{n.kind.toLowerCase()}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mint new node */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{isSense ? t('skillsTaxonomy.mintNewSense') : t('skillsTaxonomy.mintNewSkill')}</p>
        <div className="flex gap-2">
          <input value={mintLabel} onChange={(e) => setMintLabel(e.target.value)} placeholder={t('skillsTaxonomy.canonicalLabelPlaceholder')}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          <button type="button" disabled={busy || !mintLabel.trim()} onClick={() => void doMint()}
            className="flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            <FontAwesomeIcon icon={faPlus} /> {t('skillsTaxonomy.mintAndLink')}
          </button>
        </div>
      </div>

      {/* Reject — durable "not vocabulary to track" (storage §4.6). Offered only on
          plain orphans: a domainless orphan already has an active (domain-tagged)
          alias, which supersedes a rejection (module spec §7.9). */}
      {!isSense && (
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400">{t('skillsTaxonomy.rejectHint')}</p>
            <button type="button" disabled={busy} onClick={() => void doReject()}
              className="flex flex-none items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
              <FontAwesomeIcon icon={faBan} /> {t('skillsTaxonomy.reject')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Domainless-alias detail ----------

function DomainlessAliasDetail({ item, busy, nodeLabel, run }: { item: DomainlessAliasItem; busy: boolean; nodeLabel: (id: string | null) => string; run: RunFn }) {
  const { t } = useTranslation();
  const meta = KIND_META.DOMAINLESS_ALIAS;
  return (
    <div className="space-y-4 p-5">
      <div className="flex items-center gap-2">
        <h2 className="font-mono text-xl font-semibold text-gray-900">&ldquo;{item.surface_form}&rdquo;</h2>
        <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${meta.badge}`}>{t(meta.labelKey)}</span>
      </div>

      <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800">{t('skillsTaxonomy.domainlessAliasCallout')}</div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.resolvedNode')}</p>
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
          <span className="text-sm font-medium text-gray-900">{item.node_label ?? nodeLabel(item.node_id)}</span>
          <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">{t('skillsTaxonomy.domainNone')}</span>
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.domainsFromSiblings')}</p>
        {item.sibling_domains.length === 0 ? (
          <p className="text-sm text-gray-400">{t('skillsTaxonomy.noSiblingDomains')}</p>
        ) : (
          <div className="space-y-2">
            {item.sibling_domains.map((d) => (
              <div key={d.node_id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faTag} className="text-purple-500" />
                  <span className="text-sm font-medium text-gray-900">{d.label}</span>
                </div>
                <button type="button" disabled={busy} onClick={() => void run(async () => { await setNodeDomain(item.node_id, d.node_id); }, t('skillsTaxonomy.domainAssigned'))}
                  className="rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50">
                  {t('skillsTaxonomy.assign')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {item.sibling_node_labels.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.siblingAliases')}</p>
          <div className="flex flex-wrap gap-1.5">
            {item.sibling_node_labels.map((s, i) => (
              <span key={`${s}-${i}`} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aliases tab
// ---------------------------------------------------------------------------

function AliasesTab({ aliases, nodes, busy, nodeLabel, run }: { aliases: SkillAlias[]; nodes: SkillNode[]; busy: boolean; nodeLabel: (id: string | null) => string; run: RunFn }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const filtered = search.trim()
    ? aliases.filter((a) => a.surface_form.toLowerCase().includes(search.trim().toLowerCase()) || nodeLabel(a.node_id).toLowerCase().includes(search.trim().toLowerCase()))
    : aliases;

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{t('skillsTaxonomy.tabAliases')}</h2>
          <p className="text-sm text-gray-500">{t('skillsTaxonomy.aliasesHint')}</p>
        </div>
        <div className="relative w-72 max-w-full">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-2.5 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('skillsTaxonomy.searchSurfaceOrNode')}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-gray-400">{t('skillsTaxonomy.noAliases')}</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {filtered.map((alias) => <AliasRow key={alias.id} alias={alias} nodes={nodes} busy={busy} nodeLabel={nodeLabel} run={run} />)}
        </div>
      )}
    </div>
  );
}

function AliasRow({ alias, nodes, busy, nodeLabel, run }: { alias: SkillAlias; nodes: SkillNode[]; busy: boolean; nodeLabel: (id: string | null) => string; run: RunFn }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<null | 'repoint' | 'relabel' | 'split'>(null);
  const [draft, setDraft] = useState('');
  const active = alias.status === 'ACTIVE';
  const close = () => { setMode(null); setDraft(''); };

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-mono text-sm font-medium text-gray-900">{alias.surface_form}</span>
          <span className="ml-2 text-xs text-gray-500">→ {nodeLabel(alias.node_id)}</span>
          {alias.assigned_by && <span className="ml-2 text-xs text-gray-400">· {alias.assigned_by}</span>}
          {!active && <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600">{t('skillsTaxonomy.deprecated')}</span>}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button type="button" className="text-gray-600 hover:text-brand-700" onClick={() => { setMode(mode === 'repoint' ? null : 'repoint'); setDraft(''); }}>{t('skillsTaxonomy.repoint')}</button>
          <button type="button" className="text-gray-600 hover:text-brand-700" onClick={() => { setMode(mode === 'relabel' ? null : 'relabel'); setDraft(nodeLabel(alias.node_id)); }}>{t('skillsTaxonomy.relabel')}</button>
          <button type="button" className="text-gray-600 hover:text-brand-700" onClick={() => { setMode(mode === 'split' ? null : 'split'); setDraft(''); }}>{t('skillsTaxonomy.split')}</button>
          <button type="button" className="text-gray-600 hover:text-red-600"
            onClick={() => void run(async () => { await setAliasActive(alias.id, !active); }, active ? t('skillsTaxonomy.deprecatedDone') : t('skillsTaxonomy.reactivatedDone'))}>
            {active ? t('skillsTaxonomy.deprecate') : t('skillsTaxonomy.reactivate')}
          </button>
        </div>
      </div>

      {mode === 'repoint' && (
        <InlineSelect nodes={nodes.filter((n) => n.id !== alias.node_id)} placeholder={t('skillsTaxonomy.repointTo')} busy={busy}
          onConfirm={(nodeId) => run(async () => { await repointAlias(alias.id, nodeId); }, t('skillsTaxonomy.repointed')).then(close)} onCancel={close} />
      )}
      {mode === 'relabel' && (
        <InlineText value={draft} setValue={setDraft} placeholder={t('skillsTaxonomy.newLabel')} busy={busy}
          onConfirm={() => run(async () => { await relabelNode(alias.node_id, draft.trim()); }, t('skillsTaxonomy.relabelled')).then(close)} onCancel={close} />
      )}
      {mode === 'split' && (
        <InlineText value={draft} setValue={setDraft} placeholder={t('skillsTaxonomy.childLabel')} busy={busy}
          onConfirm={() => run(async () => { await splitAlias(alias.id, draft.trim()); }, t('skillsTaxonomy.splitDone')).then(close)} onCancel={close} />
      )}
    </div>
  );
}

function InlineText({ value, setValue, placeholder, busy, onConfirm, onCancel }: { value: string; setValue: (v: string) => void; placeholder: string; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 flex gap-2">
      <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
      <button type="button" disabled={busy || !value.trim()} onClick={onConfirm} className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">{t('common.save')}</button>
      <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button>
    </div>
  );
}

function InlineSelect({ nodes, placeholder, busy, onConfirm, onCancel }: { nodes: SkillNode[]; placeholder: string; busy: boolean; onConfirm: (nodeId: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  return (
    <div className="mt-2 flex gap-2">
      <select value={value} onChange={(e) => setValue(e.target.value)} className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm">
        <option value="">{placeholder}</option>
        {nodes.map((n) => <option key={n.id} value={n.id}>{n.canonical_label}</option>)}
      </select>
      <button type="button" disabled={busy || !value} onClick={() => onConfirm(value)} className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">{t('common.save')}</button>
      <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nodes tab
// ---------------------------------------------------------------------------

function NodesTab({ nodes, edges, busy, nodeLabel, run }: { nodes: SkillNode[]; edges: SkillEdge[]; busy: boolean; nodeLabel: (id: string | null) => string; run: RunFn }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const parentsByNode = useMemo(() => {
    const m = new Map<string, string[]>();
    edges.forEach((e) => { const arr = m.get(e.from_node) ?? []; arr.push(e.to_node); m.set(e.from_node, arr); });
    return m;
  }, [edges]);
  // A node *is* a domain when something points at it as a domain (its id appears
  // as some node's domain_id) — including self-bootstrapped nodes. Not the same as
  // a node that merely *has* a domain (is domain-sensitive).
  const domainNodes = useMemo(() => {
    const targetIds = new Set(nodes.map((n) => n.domain_id).filter((d): d is string => d != null));
    return nodes.filter((n) => targetIds.has(n.id));
  }, [nodes]);
  const filtered = search.trim() ? nodes.filter((n) => n.canonical_label.toLowerCase().includes(search.trim().toLowerCase())) : nodes;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">{t('skillsTaxonomy.nodesHint')}</p>
        <div className="relative w-72 max-w-full">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3 top-2.5 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('skillsTaxonomy.searchNodes')} className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-400">{t('skillsTaxonomy.noNodes')}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((node) => (
            <NodeCard key={node.id} node={node} parentIds={parentsByNode.get(node.id) ?? []} allNodes={nodes} domainNodes={domainNodes} busy={busy} nodeLabel={nodeLabel} run={run} />
          ))}
        </div>
      )}
    </div>
  );
}

function NodeCard({ node, parentIds, allNodes, domainNodes, busy, nodeLabel, run }: { node: SkillNode; parentIds: string[]; allNodes: SkillNode[]; domainNodes: SkillNode[]; busy: boolean; nodeLabel: (id: string | null) => string; run: RunFn }) {
  const { t } = useTranslation();
  const [editingLabel, setEditingLabel] = useState(false);
  const [label, setLabel] = useState(node.canonical_label);
  const [addParent, setAddParent] = useState('');
  // "Is a domain" (the checkbox) = self-bootstrapped (domain_id points to itself).
  // A node that *belongs to* another domain has domain_id set to that other node,
  // which makes it domain-sensitive — not a domain itself.
  const isDomain = node.domain_id === node.id;
  const belongsToDomainId = node.domain_id && node.domain_id !== node.id ? node.domain_id : '';
  const parentOptions = allNodes.filter((n) => n.id !== node.id && !parentIds.includes(n.id));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {editingLabel ? (
            <>
              <input value={label} onChange={(e) => setLabel(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-sm font-medium" />
              <button type="button" disabled={busy || !label.trim()} onClick={() => void run(async () => { await relabelNode(node.id, label.trim()); }, t('skillsTaxonomy.relabelled')).then(() => setEditingLabel(false))}
                className="rounded-md bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50">{t('common.save')}</button>
              <button type="button" onClick={() => { setEditingLabel(false); setLabel(node.canonical_label); }} className="text-xs text-gray-500">{t('common.cancel')}</button>
            </>
          ) : (
            <button type="button" onClick={() => setEditingLabel(true)} className="text-sm font-semibold text-gray-900 hover:underline" title={t('skillsTaxonomy.relabel')}>{node.canonical_label}</button>
          )}
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">{node.kind.toLowerCase()}</span>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={isDomain} disabled={busy}
            onChange={(e) => void run(async () => { await setNodeDomain(node.id, e.target.checked ? node.id : null); }, e.target.checked ? t('skillsTaxonomy.domainAssigned') : t('skillsTaxonomy.domainCleared'))}
            className="rounded border-gray-300" />
          {t('skillsTaxonomy.markDomain')}
        </label>
      </div>

      <div className="mt-3 grid gap-4 md:grid-cols-2">
        {/* Domain */}
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.domain')}</p>
          <select value={belongsToDomainId} disabled={busy}
            onChange={(e) => void run(async () => { await setNodeDomain(node.id, e.target.value || null); }, e.target.value ? t('skillsTaxonomy.domainAssigned') : t('skillsTaxonomy.domainCleared'))}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm">
            <option value="">{t('skillsTaxonomy.domainNoneOption')}</option>
            {domainNodes.filter((d) => d.id !== node.id).map((d) => <option key={d.id} value={d.id}>{d.canonical_label}</option>)}
          </select>
        </div>

        {/* Parents */}
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('skillsTaxonomy.parents')}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {parentIds.map((pid) => (
              <span key={pid} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                <FontAwesomeIcon icon={faArrowUp} className="text-gray-400" />
                {nodeLabel(pid)}
                <button type="button" disabled={busy} onClick={() => void run(async () => { await removeParentEdge(node.id, pid); }, t('skillsTaxonomy.parentRemoved'))} className="text-gray-400 hover:text-red-600" aria-label={t('common.delete')}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
            <select value={addParent} disabled={busy}
              onChange={(e) => { const v = e.target.value; setAddParent(''); if (v) void run(async () => { await addParentEdge(node.id, v); }, t('skillsTaxonomy.parentAdded')); }}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600">
              <option value="">{t('skillsTaxonomy.addParentOption')}</option>
              {parentOptions.map((n) => <option key={n.id} value={n.id}>{n.canonical_label}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
